import { useCallback, useContext, useEffect, useMemo, useReducer } from "react";

import { useTranslation } from "react-i18next";

import AuthContext from "context/auth/authContext";
import ParamsContext from "context/params/paramsContext";
import {
    ADD_BLINKED_ROW,
    DELETE_MULTIPLE_ROWS,
    DESELECT_ALL_AND_SELECT_MULTIPLE,
    DESELECT_ALL_ROWS,
    DESELECT_MULTIPLE_ROWS,
    REMOVE_BLINKED_ROW,
    RESET_INNER_TABLE_ALL_PARAMS,
    RESET_INNER_TABLE_FILTER_PARAMS,
    RESTORE_ACTIVE_COLUMNS,
    SELECT_MULTIPLE_ROWS,
    SET_CUSTOM_RESOURCE,
    SET_DETAILS_DATA,
    SET_INNER_TABLE_PARAMS,
    SET_TABLE_DATA,
    SET_TABLE_LOADING,
    TOGGLE_ACTIVE_COLUMN,
    TOGGLE_SELECTION_OF_SINGLE_ROW,
    UPDATE_SINGLE_ROW_DATA
} from "context/table/tableActions";
import TableContext from "context/table/tableContext";
import TableReducer from "context/table/tableReducer";
import {
    SetInnerTableParamsOptions,
    TableStateProps
} from "context/table/types";
import TriSourceState from "context/triSource/TriSourceState";
import {
    getStringifiedParams,
    handleSearchQueryParams,
    NOT_FILTER_NAMES,
    ORDERS,
    SORT_RESOURCES
} from "shared";

import ActionBar from "../Actions/ActionBar";
import { useTableFunctions } from "./hooks";
import Table from "./Table";

const TableState = ({
    name,
    idType,
    hasNoPermissionToRead,
    nameForFilters,
    isInnerTable,
    isNotSelectable,
    hideSearch,
    hideFilters,
    customResource,
    sortFromNewest,
    innerTableQueryParams,
    hasLocalFiltering,
    initialColumns,
    inlineActions,
    customTable,
    hasActionBar,
    hasNoToolbar,
    hasNoPagination,
    tabFilters,
    hasDetails,
    customStyle,
    outerDetailsData,
    customData,
    isCustomDataLoading,
    fixedColumnWidth,
    getInnerTableData
}: TableStateProps) => {
    const { tableRowDeselectionToggle, setQueryParams } =
        useContext(ParamsContext);

    const { user } = useContext(AuthContext);

    const { t } = useTranslation();
    const { renderColumnsWithIsActive } = useTableFunctions();

    const getInitialInnerTableQueryParams = () => {
        const sortParams = sortFromNewest
            ? {
                  [NOT_FILTER_NAMES.Order]: ORDERS.Descending,
                  [NOT_FILTER_NAMES.Sort]: SORT_RESOURCES.Id
              }
            : {};

        const otherParams = innerTableQueryParams || {};

        return { ...sortParams, ...otherParams };
    };

    const initialInnerTableQueryParams = getInitialInnerTableQueryParams();

    const initialInnerTableStringParams = getStringifiedParams(
        initialInnerTableQueryParams
    );

    const initialState = {
        tableName: name,
        tableNameForFilters: nameForFilters || null,
        hasNoPermissionToRead: hasNoPermissionToRead || false,
        idType,
        isInnerTable: Boolean(isInnerTable),
        isNotSelectable: Boolean(isNotSelectable),
        hideSearch: Boolean(hideSearch),
        hideFilters: Boolean(hideFilters),
        customResource: customResource || "",
        sortFromNewest: Boolean(sortFromNewest),
        hasNoToolbar: Boolean(hasNoToolbar),
        hasNoPagination: Boolean(hasNoPagination),
        hasDetails: Boolean(hasDetails),
        columns: initialColumns
            ? renderColumnsWithIsActive(name, initialColumns)
            : [],
        rows: [],
        innerTableInitialRows: [],
        selectableRows: [],
        selectedRows: [],
        inlineActions: inlineActions || [],
        detailsData: null,
        outerDetailsData: outerDetailsData || null,
        customData: customData || null,
        fixedColumnWidth: fixedColumnWidth ?? null,
        noResultsReason: null,
        innerTableQueryParams: initialInnerTableQueryParams,
        innerTableStringParams: initialInnerTableStringParams,
        innerTableFilteredValues: [],
        innerTableInitialParamNames: Object.keys(
            initialInnerTableQueryParams
        ) as any,
        hasLocalFiltering: Boolean(hasLocalFiltering),
        currentPage: 1,
        rowsPerPage: 25,
        totalRows: 0,
        blinkedRows: [],
        isInitialLoading: !hasNoPermissionToRead,
        isTableLoading: !hasNoPermissionToRead
    };

    const [state, dispatch] = useReducer(TableReducer, initialState);

    useEffect(() => {
        deselectAllRows();
    }, [tableRowDeselectionToggle]);

    useEffect(() => {
        innerTableQueryParams &&
            setInnerTableParams({ params: innerTableQueryParams });
        // eslint-disable-next-line
    }, [innerTableQueryParams]);

    useEffect(() => {
        customResource && setCustomResource(customResource);
    }, [customResource]);

    useEffect(() => {
        customData &&
            setTableData({
                rows: customData,
                customData,
                isLoading: isCustomDataLoading ?? false
            });
        // eslint-disable-next-line
    }, [customData, isCustomDataLoading]);

    const setTableData = useCallback(
        (data: any) =>
            dispatch({
                type: SET_TABLE_DATA,
                payload: {
                    ...data,
                    outerDetailsData,
                    user,
                    t
                }
            }),
        [outerDetailsData, user, t]
    );

    const setInnerTableParams = useCallback(
        (parameters: SetInnerTableParamsOptions) => {
            const { params, resetPage, filteredValues } = parameters;
            const { query, query_field } = params;

            const updatedParams = {
                ...params,
                ...handleSearchQueryParams(query, query_field),
                ...(Boolean(resetPage) && { page: "" })
            };

            dispatch({
                type: SET_INNER_TABLE_PARAMS,
                payload: {
                    updatedParams,
                    filteredValues,
                    t
                }
            });
        },
        [t]
    );

    const setCustomResource = (resource: string) =>
        dispatch({ type: SET_CUSTOM_RESOURCE, payload: resource });

    const resetInnerTableAllParams = useCallback(
        () => dispatch({ type: RESET_INNER_TABLE_ALL_PARAMS, payload: t }),
        [t]
    );

    const resetInnerTableFilterParams = useCallback(
        () => dispatch({ type: RESET_INNER_TABLE_FILTER_PARAMS, payload: t }),
        [t]
    );

    const toggleActiveColumn = (columnName: string) =>
        dispatch({ type: TOGGLE_ACTIVE_COLUMN, payload: columnName });

    const restoreActiveColumns = () =>
        dispatch({ type: RESTORE_ACTIVE_COLUMNS });

    const setTableLoading = (value: boolean) =>
        dispatch({ type: SET_TABLE_LOADING, payload: value });

    const toggleSelectionOfSingleRow = (row: any) =>
        dispatch({ type: TOGGLE_SELECTION_OF_SINGLE_ROW, payload: row });

    const selectMultipleRows = (rows: any) =>
        dispatch({ type: SELECT_MULTIPLE_ROWS, payload: rows });

    const deselectMultipleRows = (rows: any) =>
        dispatch({ type: DESELECT_MULTIPLE_ROWS, payload: rows });

    const deselectAllAndSelectMultiple = (rows: any) =>
        dispatch({ type: DESELECT_ALL_AND_SELECT_MULTIPLE, payload: rows });

    const deselectAllRows = () => dispatch({ type: DESELECT_ALL_ROWS });

    const setDetailsData = useCallback(
        (detailsData: any) => {
            setQueryParams({
                params: {
                    [NOT_FILTER_NAMES.Selected]: detailsData
                        ? String(detailsData[state.idType])
                        : ""
                }
            });

            dispatch({
                type: SET_DETAILS_DATA,
                payload: detailsData
            });
        },
        [state.idType, setQueryParams]
    );

    const updateSingleRowData = useCallback(
        (data: any) => {
            dispatch({
                type: UPDATE_SINGLE_ROW_DATA,
                payload: { data, user }
            });
        },
        [user]
    );

    const deleteMultipleRows = useCallback(
        (id: number[]) => {
            dispatch({ type: DELETE_MULTIPLE_ROWS, payload: { id, user } });
        },
        [user]
    );

    const addBlinkedRow = (rowId: number) =>
        dispatch({ type: ADD_BLINKED_ROW, payload: rowId });

    const removeBlinkedRow = (rowId: number) =>
        dispatch({ type: REMOVE_BLINKED_ROW, payload: rowId });

    const value = useMemo(
        () => ({
            tableName: state.tableName,
            tableNameForFilters: state.tableNameForFilters,
            hasNoPermissionToRead: state.hasNoPermissionToRead,
            idType: state.idType,
            isInnerTable: state.isInnerTable,
            isNotSelectable: state.isNotSelectable,
            hideSearch: state.hideSearch,
            hideFilters: state.hideFilters,
            customResource: state.customResource,
            sortFromNewest: state.sortFromNewest,
            hasNoToolbar: state.hasNoToolbar,
            hasNoPagination: state.hasNoPagination,
            hasDetails: state.hasDetails,
            columns: state.columns,
            rows: state.rows,
            innerTableInitialRows: state.innerTableInitialRows,
            inlineActions: state.inlineActions,
            selectableRows: state.selectableRows,
            selectedRows: state.selectedRows,
            detailsData: state.detailsData,
            outerDetailsData: state.outerDetailsData,
            customData: state.customData,
            fixedColumnWidth: state.fixedColumnWidth,
            noResultsReason: state.noResultsReason,
            innerTableQueryParams: state.innerTableQueryParams,
            innerTableStringParams: state.innerTableStringParams,
            innerTableFilteredValues: state.innerTableFilteredValues,
            innerTableInitialParamNames: state.innerTableInitialParamNames,
            hasLocalFiltering: state.hasLocalFiltering,
            currentPage: state.currentPage,
            rowsPerPage: state.rowsPerPage,
            totalRows: state.totalRows,
            blinkedRows: state.blinkedRows,
            isInitialLoading: state.isInitialLoading,
            isTableLoading: state.isTableLoading,
            setTableData,
            setInnerTableParams,
            resetInnerTableAllParams,
            resetInnerTableFilterParams,
            toggleActiveColumn,
            restoreActiveColumns,
            setTableLoading,
            toggleSelectionOfSingleRow,
            selectMultipleRows,
            deselectMultipleRows,
            deselectAllAndSelectMultiple,
            deselectAllRows,
            setDetailsData,
            updateSingleRowData,
            deleteMultipleRows,
            getInnerTableData,
            addBlinkedRow,
            removeBlinkedRow
        }),
        [
            state.tableName,
            state.tableNameForFilters,
            state.hasNoPermissionToRead,
            state.idType,
            state.isInnerTable,
            state.isNotSelectable,
            state.hideSearch,
            state.hideFilters,
            state.customResource,
            state.sortFromNewest,
            state.hasNoToolbar,
            state.hasNoPagination,
            state.hasDetails,
            state.columns,
            state.rows,
            state.innerTableInitialRows,
            state.inlineActions,
            state.selectableRows,
            state.selectedRows,
            state.detailsData,
            state.outerDetailsData,
            state.customData,
            state.fixedColumnWidth,
            state.noResultsReason,
            state.innerTableQueryParams,
            state.innerTableStringParams,
            state.innerTableFilteredValues,
            state.innerTableInitialParamNames,
            state.hasLocalFiltering,
            state.currentPage,
            state.rowsPerPage,
            state.totalRows,
            state.blinkedRows,
            state.isInitialLoading,
            state.isTableLoading,
            setTableData,
            setDetailsData,
            updateSingleRowData,
            deleteMultipleRows,
            getInnerTableData,
            setInnerTableParams,
            resetInnerTableAllParams,
            resetInnerTableFilterParams
        ]
    );

    return (
        <TableContext value={value}>
            {hasActionBar && (
                <TriSourceState>
                    <ActionBar />
                </TriSourceState>
            )}
            {tabFilters}
            {customTable || <Table customStyle={customStyle} />}
        </TableContext>
    );
};

export default TableState;
export * from "./components";
export * from "./hooks";
